/**
 * The accounting mailbox's reading (piece 32; T346; decision 1515). Opening a
 * message in the mailbox whose address `accounting.inboxAddress` holds reads
 * its sender and its attachments and says what the message looks like: a bill
 * against one of our purchase orders, a bill from a vendor, a remittance for
 * one of our invoices, a statement, or nothing it recognises. 🔨 Nothing is
 * made until a person presses Confirm or one of the Add new actions beside it.
 *
 * This file holds the pure half: what a document's text says, which of the five
 * the facts amount to, and the addresses of the forms the actions open.
 */
import { receiptFactsFromText } from '../accounting/expenseEntry';
import { mailMessagePath } from './client';
import type { DocumentInboxClassification } from '../accounting/documentsInbox';
import type { InboxStamp } from './inboxStamp';

/** 🔨 Relative to the API root, as every browser path is. */
export const mailAccountingReadingPath = (id: string): string => `${mailMessagePath(id)}/accounting-reading`;
/** Confirm on a bill against an order: the Bill is built from the order at once. */
export const mailAccountingConfirmPath = (id: string): string => `${mailMessagePath(id)}/accounting-confirm`;
/** After a form opened from the block saves: the message's PDF is attached to the new document and the message tagged to it. */
export const mailAccountingFilePath = (id: string): string => `${mailMessagePath(id)}/accounting-file`;
/** Add new expense: the message's PDF waits in the Documents Inbox as the person's own receipt. */
export const mailAccountingReceiptPath = (id: string): string => `${mailMessagePath(id)}/accounting-receipt`;

/** The five things a message in the accounting mailbox can look like (decision 1515). */
export const ACCOUNTING_READING_KINDS = ['billAgainstOrder', 'billFromVendor', 'remittance', 'statement', 'nothing'] as const;
export type AccountingReadingKind = (typeof ACCOUNTING_READING_KINDS)[number];

/**
 * Why a reading says nothing it recognises: the attachment is a picture and no
 * Vision key is held, the message carries no PDF or picture, the PDF carries no
 * text, or the text names nothing here.
 */
export type AccountingNothingReason = 'noVisionKey' | 'noDocument' | 'unread' | 'unrecognised';

/** The document records a message in the accounting mailbox can be filed against, each one a tag the message can carry. */
export const ACCOUNTING_FILED_RECORD_TYPES = ['Bill', 'Payment', 'VendorCredit', 'ExpenseLine'] as const;
export type AccountingFiledRecordType = (typeof ACCOUNTING_FILED_RECORD_TYPES)[number];

/**
 * 🔒 The records a form opened from the block files a message against by id (T3498): an expense line is made
 * and filed in one step by Add New Expense and is never named by a caller, so the file route refuses it.
 */
export const ACCOUNTING_FILE_ROUTE_TYPES = ['Bill', 'Payment', 'VendorCredit'] as const;

/**
 * 🔒 The dealt-with mark is read off the tag and stored nowhere (decision 1515):
 * a message in the accounting mailbox that carries a tag a person applied to a
 * Bill, a Payment, a Vendor Credit or an expense line (decision 10042) has been
 * dealt with. An inherited tag marks nothing.
 */
export function isDealtWith(input: { inAccountingMailbox: boolean; taggedRecordType: string | null; taggedRecordInherited: boolean }): boolean {
  return input.inAccountingMailbox && !input.taggedRecordInherited && (ACCOUNTING_FILED_RECORD_TYPES as readonly string[]).includes(input.taggedRecordType ?? '');
}

/** What the text of one document says, each null where the text does not say it plainly. */
export interface AccountingDocumentFacts {
  /** The sender's own number for the document: the vendor's invoice number on a bill. */
  documentNumber: string | null;
  /** The document's date as a calendar day. */
  documentDate: string | null;
  totalCents: number | null;
  /** The text calls itself a statement of account. */
  statement: boolean;
}

/**
 * How the reading matched the company (decision 1515): the sender's address,
 * the company line of the sender's signature, or the document's letterhead
 * alone, which the mail itself did not back.
 */
export const ACCOUNTING_COMPANY_SOURCES = ['sender', 'signature', 'letterhead'] as const;
export type AccountingCompanySource = (typeof ACCOUNTING_COMPANY_SOURCES)[number];

/** The company the sender resolves to, as the reading names it, and how it was matched. */
export interface AccountingReadingCompany {
  id: string;
  displayName: string;
  isVendor: boolean;
  isCustomer: boolean;
  matchedBy: AccountingCompanySource;
}

/**
 * 🔒 The source a match is named by (decision 1515): a domain match is the
 * sender's address, and a name match is the line it was read from, the
 * signature's company line or the document's letterhead.
 */
export function accountingCompanySource(companyFrom: 'signature' | 'domain', line: 'signature' | 'letterhead'): AccountingCompanySource {
  return companyFrom === 'domain' ? 'sender' : line;
}

/** One of the vendor's purchase orders the Bill form would offer (T140). */
export interface AccountingOpenOrder {
  id: string;
  displayNumber: string;
  vendorCompanyId: string;
  unbilledCents: number;
}

/** One of our own invoices the text names. */
export interface AccountingNamedInvoice {
  id: string;
  invoiceNumber: string;
  companyId: string;
  companyName: string;
}

/** The server's answer for one message in the accounting mailbox. */
export interface AccountingReading {
  messageId: string;
  kind: AccountingReadingKind;
  /** Set only where `kind` is `nothing`. */
  reason: AccountingNothingReason | null;
  facts: AccountingDocumentFacts;
  company: AccountingReadingCompany | null;
  purchaseOrder: AccountingOpenOrder | null;
  invoice: AccountingNamedInvoice | null;
  /** How many open orders the vendor holds, which the Bill form offers whatever the suggestion. */
  openOrderCount: number;
  /** 🔒 The other live Bill already carrying the vendor's invoice number this message reads, or null; Confirm asks before a second one is made (T1041; decision 10860). */
  duplicateInvoice: { billId: string; billNumber: string } | null;
  /** The attachment the reading read and Confirm attaches; null where none was read. */
  attachment: { id: string; filename: string } | null;
  /** The record the message is already tagged to, where it is a Bill or a Payment. */
  filed: { recordType: AccountingFiledRecordType; recordId: string; label: string } | null;
  /** Whether the viewer may write accounting documents; the actions are withheld otherwise. */
  canAct: boolean;
  /**
   * 🔒 The dedicated address the message was delivered to and the classification it stamps, found from the message's
   * addresses before its document is read, so it stands whatever the reading gives (T3432; decision 9800). Null where
   * the message reached no active dedicated address.
   */
  stamp: InboxStamp | null;
}

/** 🔒 Send to accounting (decision 1516): the message forwarded, attachments and all, to the accounting mailbox. */
export const mailSendToAccountingPath = (id: string): string => `/mail/messages/${id}/send-to-accounting`;

/** What Send to accounting answers: the forward it sent, whether it went or waits in Outbox, and where. */
export interface SendToAccountingAnswer {
  id: string;
  outcome: 'Sent' | 'Held' | 'Failed';
  address: string;
}

/** What Confirm does for each kind, or null where there is nothing to confirm. */
export type AccountingConfirm = 'createBill' | 'openBill' | 'openPayment' | null;

/**
 * 🔒 Confirm on a bill against an order builds the Bill at once only where the
 * text gives the vendor's invoice number, which the build needs; without it the
 * Bill form opens on the order. A plain bill opens the form, a remittance opens
 * the payment, and a statement or nothing offers no Confirm.
 */
export function accountingConfirm(reading: Pick<AccountingReading, 'kind' | 'facts' | 'purchaseOrder'>): AccountingConfirm {
  if (reading.kind === 'billAgainstOrder') return reading.purchaseOrder && reading.facts.documentNumber ? 'createBill' : 'openBill';
  if (reading.kind === 'billFromVendor') return 'openBill';
  if (reading.kind === 'remittance') return 'openPayment';
  return null;
}

const NUMBER_LABEL = /\b(?:invoice|bill|credit\s+memo|credit\s+note)\b\s*(?:#|no\.?|num\.?|number)?\s*:?\s*#?\s*([A-Z0-9][A-Z0-9\-/]*\d[A-Z0-9\-/]*)/i;
const DATE_LABEL = /\b(?:invoice|bill|document|statement|payment|remittance)?\s*date\b/i;
const ISO_DAY = /\b(\d{4})-(\d{2})-(\d{2})\b/;
const US_DAY = /\b(\d{1,2})\/(\d{1,2})\/(\d{4})\b/;
const STATEMENT_LINE = /^\s*(?:account\s+)?statement\b|\bstatement\s+of\s+account\b/i;
const PAID_LINE = /\b(?:amount\s+paid|payment\s+amount|total\s+paid|amount\s+remitted)\b/i;
const AMOUNT = /\$?\s*(\d{1,3}(?:,\d{3})+|\d+)\.(\d{2})\b/g;

function dayOf(text: string): string | null {
  const iso = ISO_DAY.exec(text);
  const us = US_DAY.exec(text);
  const isoAt = iso ? iso.index : Infinity;
  const usAt = us ? us.index : Infinity;
  if (iso && isoAt <= usAt) {
    const [, y, m, d] = iso;
    return Number(m) >= 1 && Number(m) <= 12 && Number(d) >= 1 && Number(d) <= 31 ? `${y}-${m}-${d}` : null;
  }
  if (us) {
    const [, m, d, y] = us;
    return Number(m) >= 1 && Number(m) <= 12 && Number(d) >= 1 && Number(d) <= 31 ? `${y}-${m!.padStart(2, '0')}-${d!.padStart(2, '0')}` : null;
  }
  return null;
}

/**
 * 🔒 The number, the date and the total a document's text says (decision 1515).
 * The number is the first one printed after an invoice or bill label; the date
 * is the first after a date label, else the first anywhere; the total is the
 * last amount on the last line labelled a total, the one a receipt's reading
 * takes (decision 1188), else the amount on a line labelled paid. 🔨 Each is
 * null where the text does not say it plainly: a guess is never offered as a read.
 */
export function accountingFactsFromText(text: string): AccountingDocumentFacts {
  const lines = text.split(/\r?\n/).map((row) => row.trim()).filter((row) => row !== '');
  let documentNumber: string | null = null;
  for (const row of lines) {
    const found = NUMBER_LABEL.exec(row);
    if (found) {
      documentNumber = found[1]!.replace(/[-/]+$/, '');
      break;
    }
  }
  let documentDate: string | null = null;
  for (const row of lines) {
    const label = DATE_LABEL.exec(row);
    if (!label) continue;
    documentDate = dayOf(row.slice(label.index));
    if (documentDate) break;
  }
  documentDate ??= dayOf(text);
  let totalCents = receiptFactsFromText(text).totalCents;
  if (totalCents === null) {
    for (const row of lines) {
      if (!PAID_LINE.test(row)) continue;
      const amounts = Array.from(row.matchAll(AMOUNT));
      const last = amounts[amounts.length - 1];
      if (last) totalCents = Number(last[1]!.replace(/,/g, '')) * 100 + Number(last[2]);
    }
  }
  return { documentNumber, documentDate, totalCents, statement: lines.some((row) => STATEMENT_LINE.test(row)) };
}

/**
 * Every token in the text that could be one of our own document numbers: a run
 * of letters, digits, dashes and slashes carrying at least one letter and one
 * digit, upper-cased, with a revision's two-digit tail also offered bare, since
 * an order prints as its number and its revision (decision 0409).
 */
export function documentNumberCandidates(text: string): string[] {
  const out = new Set<string>();
  for (const raw of text.split(/[^A-Za-z0-9\-/]+/)) {
    const token = raw.replace(/^[-/]+|[-/]+$/g, '').toUpperCase();
    if (token.length < 3 || token.length > 40 || !/[A-Z]/.test(token) || !/\d/.test(token)) continue;
    out.add(token);
    const bare = /^(.*\d)-\d{2}$/.exec(token);
    if (bare) out.add(bare[1]!);
  }
  return [...out];
}

/** What the reading found beyond the text: the sender's company, the order and the invoice the text names, and the vendor's open orders. */
export interface AccountingLookups {
  company: AccountingReadingCompany | null;
  /** An order of ours the text names that is open to bill. */
  namedOrder: AccountingOpenOrder | null;
  /** An invoice of ours the text names. */
  namedInvoice: AccountingNamedInvoice | null;
  /** The sender company's orders open to bill (T140). */
  openOrders: readonly AccountingOpenOrder[];
  /** The classification a dedicated address stamped on the message, its first guess (decision 9800). */
  stamp?: DocumentInboxClassification | null;
}

/**
 * 🔒 Which of the five a read document amounts to (decision 1515), in order:
 * - a statement says so, and makes nothing;
 * - an open order of ours the text names, from its own vendor or a sender no company here answers to, is a bill against it;
 * - an invoice of ours the text names, from its own customer or such a sender, is a remittance for it;
 * - a vendor's document is a bill against the one open order whose remainder is its total, else against its only open order, else a bill from the vendor;
 * - anything else is nothing it recognises.
 * 🔨 A vendor with two or more open orders and no number or total to tell them apart is a bill from the vendor, and the Bill form offers the orders.
 *
 * 🔒 A Vendor bill stamp is the first guess (decision 9800): the document is a bill, and the reading fills only the
 * order, a named one first and then the vendor's, so a stamped document is never read as a statement, a remittance
 * or nothing. The other classifications name no kind of the five, so the reading's own answer stands beside them.
 */
export function accountingVerdict(facts: AccountingDocumentFacts, found: AccountingLookups): Pick<AccountingReading, 'kind' | 'reason' | 'purchaseOrder' | 'invoice'> {
  const none = { purchaseOrder: null, invoice: null };
  const stampedBill = found.stamp === 'VendorBill';
  if (facts.statement && !stampedBill) return { kind: 'statement', reason: null, ...none };
  const sender = found.company;
  if (found.namedOrder && (!sender || sender.id === found.namedOrder.vendorCompanyId)) return { kind: 'billAgainstOrder', reason: null, purchaseOrder: found.namedOrder, invoice: null };
  if (found.namedInvoice && !stampedBill && (!sender || sender.id === found.namedInvoice.companyId)) return { kind: 'remittance', reason: null, purchaseOrder: null, invoice: found.namedInvoice };
  if (sender && (sender.isVendor || found.openOrders.length > 0 || stampedBill)) {
    const byTotal = facts.totalCents === null ? [] : found.openOrders.filter((order) => order.unbilledCents === facts.totalCents);
    const order = byTotal.length === 1 ? byTotal[0]! : found.openOrders.length === 1 ? found.openOrders[0]! : null;
    if (order) return { kind: 'billAgainstOrder', reason: null, purchaseOrder: order, invoice: null };
    return { kind: 'billFromVendor', reason: null, ...none };
  }
  if (stampedBill) return { kind: 'billFromVendor', reason: null, ...none };
  return { kind: 'nothing', reason: 'unrecognised', ...none };
}

/** The kinds of document the block's actions open: its suggestion's Confirm and the four Add new alternatives. */
export type AccountingFormKind = 'bill' | 'payment';

/**
 * 🔒 The address of the form an action opens, carrying what the reading found
 * (decision 1515): the vendor or the account, the order or the invoice, the
 * number, the date and the amount, and the message the form files itself
 * against once it saves. A value the reading did not find is left off.
 */
export function accountingFormPath(kind: AccountingFormKind, reading: Pick<AccountingReading, 'messageId' | 'facts' | 'company' | 'purchaseOrder' | 'invoice'>): string {
  const params = new URLSearchParams();
  const set = (key: string, value: string | number | null | undefined): void => {
    if (value !== null && value !== undefined && value !== '') params.set(key, String(value));
  };
  if (kind === 'bill') {
    set('vendorId', reading.purchaseOrder?.vendorCompanyId ?? reading.company?.id);
    set('purchaseOrderId', reading.purchaseOrder?.id);
    set('vendorInvoiceNumber', reading.facts.documentNumber);
    set('billDate', reading.facts.documentDate);
    // 🔨 An order fills the lines from what is left on it, so the total rides only where no order does.
    if (!reading.purchaseOrder) set('amountCents', reading.facts.totalCents);
  } else {
    set('companyId', reading.invoice?.companyId ?? reading.company?.id);
    set('invoiceId', reading.invoice?.id);
    set('direction', reading.invoice || !reading.company || reading.company.isCustomer || !reading.company.isVendor ? 'Received' : 'Sent');
    set('amountCents', reading.facts.totalCents);
    set('paymentDate', reading.facts.documentDate);
  }
  set('fromMessage', reading.messageId);
  return `${kind === 'bill' ? '/bills' : '/payments'}/new?${params.toString()}`;
}

/** A whole number of cents read off an address, or null where it carries none. */
export function centsParam(value: string | null): number | null {
  if (value === null || !/^\d{1,12}$/.test(value)) return null;
  return Number(value);
}

/** A calendar day read off an address, or null where it carries none. */
export function dayParam(value: string | null): string | null {
  return value !== null && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}
